import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDocumentMeta } from '../lib/use-document-meta.js';
import { metaForPath } from '../lib/route-meta.js';
import './CaseStudies.css';

// Public index page for case studies. Lists every published row.
export default function CaseStudies() {
  useDocumentMeta(metaForPath('/case-studies'));

  const [state, setState] = useState({ status: 'loading', items: [], error: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/list-case-studies');
        if (cancelled) return;
        if (!res.ok) {
          setState({ status: 'error', items: [], error: `Could not load (${res.status})` });
          return;
        }
        const json = await res.json();
        setState({ status: 'ready', items: json.items || [], error: null });
      } catch (err) {
        if (cancelled) return;
        setState({ status: 'error', items: [], error: err.message || 'Network error' });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="cs-page">
      <header className="cs-header">
        <Link to="/" className="cs-brand">margin</Link>
        <Link to="/#offer" className="cs-cta">Start a conversation</Link>
      </header>

      <main className="cs-main">
        <div className="cs-kicker">Case studies</div>
        <h1 className="cs-h1">Four engagements. Nobody could scope any of them.</h1>
        <p className="cs-lede">
          Every engagement starts the same way. I go look at how the operation actually runs, not the version in the documentation. I name the problem in a version all sides recognize. Then I draw the fix, build it, and hand it off. An attraction, a fleet, a public infrastructure program, and a franchise owner working alone.
        </p>

        {state.status === 'loading' && (
          <div className="cs-state">Loading…</div>
        )}

        {state.status === 'error' && (
          <div className="cs-state">Couldn't load right now. {state.error}</div>
        )}

        {state.status === 'ready' && state.items.length === 0 && (
          <div className="cs-state">
            I'm writing these up now. Check back soon, or
            {' '}<Link to="/#offer" className="cs-link">start your own conversation</Link>.
          </div>
        )}

        {state.status === 'ready' && state.items.length > 0 && (
          <ul className="cs-list">
            {state.items.map((item) => (
              <li key={item.slug} className="cs-card">
                <Link to={`/case-studies/${item.slug}`} className="cs-card-link">
                  {item.hero_kicker && <div className="cs-card-kicker">{item.hero_kicker}</div>}
                  <div className="cs-card-name">{item.display_name}</div>
                  <div className="cs-card-business">
                    {item.display_business}
                    {item.display_city && <span className="cs-card-sep"> · {item.display_city}</span>}
                  </div>
                  {item.summary && <p className="cs-card-summary">{item.summary}</p>}
                  <div className="cs-card-more">Read the engagement →</div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}
